import {
  Auth0Client,
  Auth0ClientOptions,
  LogoutOptions,
  RedirectLoginOptions,
  createAuth0Client,
} from '@auth0/auth0-spa-js';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import {
  configureTokenBroker,
  cancelTokenRefreshOnLogout,
} from 'services/auth/tokenBroker';
import { pushTokenOnResume, serveTokenRequests } from 'services/auth/tokenServer';
import queryString from 'query-string';
import { useNavigate, useLocation } from 'react-router-dom';
import { authSessionClient } from 'shared/domain/auth/authSessionClient';
import { debugLog } from 'shared/logger/debugLog';
import { projectClient } from 'shared/domain/project/projectClient';
import {
  isLogoutFlowInProgress,
  markLogoutFlowCompleted,
} from 'components/core/WithLogout/model';
import { requestResyncAfterResume } from 'services/serviceWorker/swConfigReady';
import { auth0Config } from './auth_config';
import {
  getTabResumeAuthAction,
  resolveInitialAuthenticatedState,
  tryRestoreAuthenticatedSession,
} from './resolveInitialAuthenticatedState';
import { bootstrapAuthenticatedSession as runBootstrapAuthenticatedSession } from './bootstrapAuthenticatedSession';
import { createTabResumeGate } from './tabResumeGate';
import { getUserStoreState } from 'store/user';

interface Auth0ContextType {
  isAuthenticated?: boolean;
  token?: string;
  loading: boolean;
  error?: boolean;
  loginWithRedirect<TAppState = any>(options?: RedirectLoginOptions<TAppState>): Promise<void> | undefined;
  logout(o?: LogoutOptions): void;
  handleRedirect(redirectUrl: string, targetUrl?: string): Promise<void>;
}

interface Auth0ProviderOptions<TAppState = any> {
  children: React.ReactElement;
  onRedirectCallback?(appState: TAppState): void;
}

const DEFAULT_REDIRECT_CALLBACK = (): void =>
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname
  );

const Auth0Context = React.createContext<Auth0ContextType | null>(null);
export const useAuth0 = (): Auth0ContextType => {
  const result = useContext(Auth0Context);

  if (!result) {
    throw new Error('useAuth0 must be used within an Auth0Provider');
  }

  return result;
};

const Auth0Provider = <TAppState = any>({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}: Auth0ProviderOptions<TAppState> & Auth0ClientOptions): React.ReactElement => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
  const [token, setToken] = useState<string>();
  const [error, setError] = useState<boolean>();

  const auth0ClientRef = useRef<Auth0Client | undefined>();
  const [auth0Client, _setAuth0] = useState<Auth0Client>();
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  const setAuth0 = useCallback((client: Auth0Client) => {
    auth0ClientRef.current = client;
    _setAuth0(client);
  }, []);

  const handleRedirect = async (redirect_uri: string, targetUrl?: string): Promise<void> => {
    try {
      if (error || !auth0Client) {
        error && debugLog('Auth0Provider: error', error);
        const auth0FromHook = await createAuth0Client(initOptions);
        setAuth0(auth0FromHook);

        return auth0FromHook.logout(initOptions);
      }
    } finally {
      setError(false);
      loginRedirect({ 
        authorizationParams: { redirect_uri },
        appState: targetUrl ? { targetUrl } : undefined
      });
    }
  };

  const saveToken = useCallback(async (forceRefresh = false) => {
    const lsToken = LocalStorageManager.getAuthToken();
    const lsExpiration = LocalStorageManager.getAuthTokenExpiration();
    const nowPlus1Minute = Date.now() + 60 * 1000;

    if (
      !forceRefresh &&
      lsToken &&
      lsExpiration &&
      parseFloat(lsExpiration) > nowPlus1Minute
    ) {
      setToken(lsToken);
      return;
    }
    
    if (!auth0ClientRef.current) {
      return;
    }
    const token = await auth0ClientRef.current.getTokenSilently(
      forceRefresh ? { cacheMode: 'off' } : undefined
    );
    const claims = await auth0ClientRef.current.getIdTokenClaims();
    if (claims) {
      LocalStorageManager.setAuthToken(token ?? '');
      LocalStorageManager.setAuthTokenExpiration(`${claims.exp}000`);
      LocalStorageManager.setUserData(claims.email ?? '', claims.sub?.slice(6) ?? '');
    }
    setToken(token);
  }, []);

  const bootstrapAuthenticatedSession = useCallback(
    async (options?: { forceTokenRefresh?: boolean }): Promise<void> => {
      await runBootstrapAuthenticatedSession(
        {
          saveToken,
          pushTokenOnResume,
          authenticate: () => getUserStoreState().actions.authenticate(),
          downloadProjectListWhenMissing: () => projectClient.downloadWhenMissing(),
          requestResyncAfterResume,
          markLogoutFlowCompleted,
        },
        options
      );
    },
    // pushTokenOnResume is a stable module-level function; omitting it is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable imports: pushTokenOnResume, projectClient, requestResyncAfterResume, markLogoutFlowCompleted
    [saveToken]
  );

  const notifyCrossTabSessionRestored = useCallback((): void => {
    authSessionClient.announceSessionRestored();
  }, []);

  const refreshSessionFromAuth0 = useCallback(async (): Promise<void> => {
    if (isLogoutFlowInProgress()) {
      return;
    }

    const authClient = auth0ClientRef.current;
    if (!authClient) {
      return;
    }

    const authenticated = await tryRestoreAuthenticatedSession(authClient);
    setIsAuthenticated(authenticated);

    if (!authenticated) {
      return;
    }

    await bootstrapAuthenticatedSession();
  }, [bootstrapAuthenticatedSession]);

  useEffect(() => {
    const initAuth0 = async (): Promise<void> => {
      try {
        const auth0FromHook = await createAuth0Client(initOptions);
        setAuth0(auth0FromHook);
        const hasAuthRedirectParams =
          window.location.search.includes('?code=') ||
          window.location.search.includes('?state=');

        if (hasAuthRedirectParams) {
          try {
            const { appState } =
              await auth0FromHook.handleRedirectCallback();
            onRedirectCallback(appState);

            // Restart service worker after Auth0 redirect
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker
                .getRegistration()
                .then((registration) => {
                  if (registration?.active) {
                    debugLog(
                      'Service worker found, checking state:',
                      registration.active.state
                    );
                    if (registration.active.state === 'redundant') {
                      debugLog(
                        'Service worker is redundant, attempting to restart...'
                      );
                      registration.update().then(() => {
                        debugLog('Service worker update triggered');
                      });
                    }
                  } else if (registration && !registration.active) {
                    debugLog(
                      'No active service worker, attempting to restart...'
                    );
                    registration.update().then(() => {
                      debugLog('Service worker update triggered');
                    });
                  }
                });
            }
          } catch (error) {
            onRedirectCallback({} as TAppState);
          }
        }
        let isAuthenticated: boolean;
        try {
          isAuthenticated = await resolveInitialAuthenticatedState(
            auth0FromHook
          );
        } catch (error) {
          debugLog(
            'Auth0Provider: failed to resolve authentication state',
            error
          );
          setError(true);
          return;
        }
        setIsAuthenticated(isAuthenticated);

        if (isAuthenticated) {
          await bootstrapAuthenticatedSession({
            forceTokenRefresh: hasAuthRedirectParams,
          });
          if (hasAuthRedirectParams) {
            notifyCrossTabSessionRestored();
          }
        }

        if (window.location.search.includes('?error')) {
          const query = queryString.parse(location.search);
          const { error, error_description } = query;

          // if (error === USER_UNAUTHORIZED) {
          //   ShowUnauthorizedToaster(error_description, dispatch);
          // } else {
          //   ShowGeneralErrorToaster(error_description, dispatch);
          // }

          setError(true);
        }
      } finally {
        setLoading(false);
      }
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  useEffect(
    function subscribeToCrossTabSessionRestored() {
      return authSessionClient.onSessionRestored(() => void refreshSessionFromAuth0());
    },
    [refreshSessionFromAuth0]
  );

  const loginRedirect = <TAppState = any>(options?: RedirectLoginOptions<TAppState>): Promise<void> | undefined => {
    if (!auth0Client) {
      return;
    }

    return auth0Client.loginWithRedirect(options);
  };

  const logout: Auth0Client['logout'] = useCallback((o) => {
    cancelTokenRefreshOnLogout();
    if (auth0ClientRef.current?.logout)
      return auth0ClientRef.current && auth0ClientRef.current.logout(o);
    throw new Error('Logout function missing');
  }, []);

  useEffect(() => {
    configureTokenBroker({
      getAuth0Client: () => auth0ClientRef.current,
      saveToken,
    });
    const stopServingToken = serveTokenRequests();

    const isNewResume = createTabResumeGate();
    const onResume = (): void => {
      if (document.hidden || !isNewResume()) {
        return;
      }

      const resumeAction = getTabResumeAuthAction(isAuthenticatedRef.current);
      if (resumeAction === 'wait') {
        return;
      }

      // Login form: silent restore only. bootstrapAuthenticatedSession already
      // calls requestResyncAfterResume — do not also run pushTokenOnResume.
      if (resumeAction === 'restore') {
        void refreshSessionFromAuth0();
        return;
      }

      pushTokenOnResume()
        .then(() => {
          requestResyncAfterResume();
        })
        .catch(() => undefined);
    };

    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);

    return () => {
      stopServingToken();
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, [refreshSessionFromAuth0, saveToken]);

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        token,
        error,
        loading,
        handleRedirect,
        loginWithRedirect: loginRedirect,
        logout: logout,
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};

interface AuthProviderProps {
  children: React.ReactNode;
}

interface AppState {
  targetUrl?: string;
}

export const AuthProvider = ({ children }: AuthProviderProps): React.ReactElement => {
  const navigate = useNavigate();
  const customRedirectCallback = useCallback(
    (appState?: AppState): void => {
      navigate(appState?.targetUrl || window.location.pathname);
    },
    [navigate]
  );

  const authorizationParams = useMemo(() => {
    return {
      redirect_uri: auth0Config.redirectUri,
      audience: auth0Config.audience,
      scope: 'openid offline_access profile email',
      prompt: 'select_account' as const,
    };
  }, [auth0Config.redirectUri, auth0Config.audience]);
  
  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={authorizationParams}
      onRedirectCallback={customRedirectCallback}
      useRefreshTokens={true}
      cacheLocation={auth0Config.cacheLocation}
      // https://community.auth0.com/t/auth0-spa-2-x-returning-missing-refresh-token/98999/18
      // https://github.com/auth0/auth0-spa-js/blob/master/MIGRATION_GUIDE.md#no-more-iframe-fallback-by-default-when-using-refresh-tokens
      useRefreshTokensFallback={true}
    >
      <>{children}</>
    </Auth0Provider>
  );
};
